import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiClock, FiFileText, FiAward, FiSearch, FiChevronLeft, FiChevronRight, FiCheckCircle, FiAlertCircle, FiEye, FiEdit3, FiUnlock } from 'react-icons/fi';
import PdfViewer from '../../components/common/PdfViewer';
import { formatUTCToChicago, getCurrentWeekNumber } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import styles from './AssignmentSubmissions.module.css';
import richTextStyles from '../../styles/richTextViewer.module.css';

// Helper function to clean names - remove trailing zeros and handle boolean/falsy values
const cleanName = (name) => {
  // Handle falsy values (null, undefined, false, 0, empty string)
  if (name === null || name === undefined || name === false || name === 0) return '';
  // Convert to string and trim
  const nameStr = String(name).trim();
  // Return empty if it's just "0" or "false"
  if (nameStr === '0' || nameStr === 'false' || nameStr === '') return '';
  // Remove trailing zeros - but only if they're truly trailing (not part of the name like "Ramzanali0")
  // Actually, we DO want to remove trailing zeros, so this is correct
  // But ensure we're not removing zeros that are part of valid names
  // The issue might be that max_weekly_submissions (0) is being concatenated somehow
  return nameStr.replace(/0+$/, '');
};

function AssignmentSubmissions() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPendingGrading = searchParams.get('from') === 'pending-grading';

  // State
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [allEnrolledStudents, setAllEnrolledStudents] = useState([]); // All students enrolled in the course
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('date-desc');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [selectedWeekTab, setSelectedWeekTab] = useState(1); // Default to Week 1 for weekly assignments
  
  // Grading state
  const [gradingData, setGradingData] = useState({});
  const [feedbackData, setFeedbackData] = useState({});
  const [editingGrade, setEditingGrade] = useState({});
  const [editingFeedback, setEditingFeedback] = useState({});
  const [savingAssessment, setSavingAssessment] = useState({});
  const [lastSaved, setLastSaved] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [allowingResubmission, setAllowingResubmission] = useState({});
  const [resubmissionStatus, setResubmissionStatus] = useState({});
  
  // PDF viewer
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedPdfFileName, setSelectedPdfFileName] = useState('document.pdf');

  // Fetch assignment and submissions
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getAssignmentSubmissions(assignmentId);
      
      if (response.success) {
        setAssignment(response.assignment);
        
        const submittedList = response.submissions || [];
        const isWeeklySubmission = !!(response.assignment?.allow_weekly_submissions === 1 || response.assignment?.allow_weekly_submissions === true);
        
        // Get ALL enrolled students by combining students from submissions and notSubmitted
        // For weekly assignments, we need all students to show them in each week tab
        const studentsFromSubmissions = submittedList.map(s => ({
          user_id: s.user_id,
          first_name: s.first_name,
          last_name: s.last_name,
          email: s.email
        }));
        
        const studentsNotSubmitted = (response.notSubmitted || []).map(s => ({
          user_id: s.user_id,
          first_name: s.first_name,
          last_name: s.last_name,
          email: s.email
        }));
        
        // Combine and deduplicate by user_id
        const allStudentsMap = new Map();
        [...studentsFromSubmissions, ...studentsNotSubmitted].forEach(student => {
          if (!allStudentsMap.has(student.user_id)) {
            allStudentsMap.set(student.user_id, student);
          }
        });
        
        const allEnrolledStudentsList = Array.from(allStudentsMap.values());
        setAllEnrolledStudents(allEnrolledStudentsList);
        
        // For weekly submissions, we need to handle multiple submissions per student
        // For non-weekly, we keep the existing logic
        const submittedUserIds = new Set(submittedList.map(s => s.user_id));
        const notSubmittedList = (response.notSubmitted || [])
          .filter(student => !submittedUserIds.has(student.user_id))
          .map(student => ({
            id: `not-submitted-${student.user_id}`,
            user_id: student.user_id,
            first_name: student.first_name,
            last_name: student.last_name,
            email: student.email,
            submitted_at: null,
            status: 'not_submitted',
            grade: null,
            feedback: null,
            content: null,
            pdf_attachment: null,
            week_number: null
          }));
        
        // For weekly submissions, include week_number in the ID to make each submission unique
        // Also ensure max_weekly_submissions from assignment doesn't leak into submission data
        const processedSubmissions = submittedList.map(sub => {
          const { max_weekly_submissions, ...submissionData } = sub;
          return {
            ...submissionData,
            id: isWeeklySubmission && sub.week_number != null && sub.week_number > 0
              ? `submission-${sub.user_id}-week-${sub.week_number}` 
              : sub.id || `submission-${sub.user_id}`
          };
        });
        
        const allSubmissions = [...processedSubmissions, ...notSubmittedList];
        setSubmissions(allSubmissions);
        
        // Auto-select first student (only if no week tab is selected or if selected student is still valid)
        if (allSubmissions.length > 0) {
          // For weekly submissions, filter submissions for the selected week
          const isWeeklySubmission = !!(response.assignment?.allow_weekly_submissions === 1 || response.assignment?.allow_weekly_submissions === true);
          let submissionsToCheck = allSubmissions;
          if (isWeeklySubmission && selectedWeekTab) {
            submissionsToCheck = allSubmissions.filter(s => s.week_number === selectedWeekTab);
          }
          
          // Check if currently selected student is still valid
          const currentSelected = allSubmissions.find(s => s.id === selectedStudentId);
          if (!currentSelected && submissionsToCheck.length > 0) {
            setSelectedStudentId(submissionsToCheck[0].id);
          } else if (!selectedStudentId && submissionsToCheck.length > 0) {
            setSelectedStudentId(submissionsToCheck[0].id);
          }
        }
      } else {
        console.error('Failed to load submissions:', response.error);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [assignmentId, selectedStudentId, selectedWeekTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isExtraCreditWeek = useCallback((weekNumber) => {
    if (!weekNumber) return false;
    const weeklyOverridesByWeek = assignment?.weekly_overrides_by_week && typeof assignment.weekly_overrides_by_week === 'object'
      ? assignment.weekly_overrides_by_week
      : {};
    const weekOverride = weeklyOverridesByWeek[weekNumber] ?? weeklyOverridesByWeek[String(weekNumber)];
    return weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1;
  }, [assignment]);

  const getEffectiveStatus = useCallback((submission) => {
    if (isExtraCreditWeek(submission?.week_number) && submission?.status === 'late') {
      return submission.grade !== null && submission.grade !== undefined ? 'graded' : 'submitted';
    }

    return submission?.status;
  }, [isExtraCreditWeek]);

  // Get filtered and sorted submissions
  const getFilteredSubmissions = useCallback(() => {
    let filtered = [...submissions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => {
        const name = `${cleanName(s.first_name)} ${cleanName(s.last_name)}`.toLowerCase();
        const email = s.email.toLowerCase();
        const grade = s.grade ? s.grade.toString() : '';
        return name.includes(term) || email.includes(term) || grade.includes(term);
      });
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => {
        switch (filterStatus) {
          case 'submitted':
            return s.submitted_at && getEffectiveStatus(s) !== 'graded';
          case 'not_submitted':
            return !s.submitted_at;
          case 'graded':
            return getEffectiveStatus(s) === 'graded';
          case 'ungraded':
            return s.submitted_at && getEffectiveStatus(s) !== 'graded';
          case 'late':
            return getEffectiveStatus(s) === 'late';
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'date-desc':
          return new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0);
        case 'date-asc':
          return new Date(a.submitted_at || 0) - new Date(b.submitted_at || 0);
        case 'name-asc':
          return `${cleanName(a.first_name)} ${cleanName(a.last_name)}`.localeCompare(`${cleanName(b.first_name)} ${cleanName(b.last_name)}`);
        case 'name-desc':
          return `${cleanName(b.first_name)} ${cleanName(b.last_name)}`.localeCompare(`${cleanName(a.first_name)} ${cleanName(a.last_name)}`);
        default:
          return 0;
      }
    });

    return filtered;
  }, [submissions, searchTerm, filterStatus, sortOrder, getEffectiveStatus]);

  // Get available weeks from submissions and assignment settings
  const getAvailableWeeks = useCallback(() => {
    const isWeeklySubmission = !!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true);
    if (!isWeeklySubmission) return [];

    const normalizedMaxWeeklySubmissions =
      Number.isFinite(Number(assignment?.max_weekly_submissions)) && Number(assignment?.max_weekly_submissions) > 0
        ? Math.floor(Number(assignment.max_weekly_submissions))
        : 1;
    
    const weekSet = new Set();
    
    // Add weeks from existing submissions
    submissions.forEach(sub => {
      const weekNum = Number(sub.week_number);
      if (Number.isFinite(weekNum) && weekNum >= 1 && weekNum <= normalizedMaxWeeklySubmissions) {
        weekSet.add(weekNum);
      }
    });
    
    // For weekly assignments, show only weeks up to the current week
    // and account for configured break periods.
    if (assignment?.start_date) {
      try {
        const normalizedBreakPeriods = Array.isArray(assignment?.break_periods)
          ? assignment.break_periods.map(bp => ({
              start: bp?.start || bp?.break_start_date,
              end: bp?.end || bp?.break_end_date
            })).filter(bp => bp.start && bp.end)
          : [];

        const currentWeek = getCurrentWeekNumber(assignment.start_date, null, normalizedBreakPeriods);
        const cappedCurrentWeek = Math.min(currentWeek, normalizedMaxWeeklySubmissions);

        // Add all weeks from 1 to current week
        for (let week = 1; week <= cappedCurrentWeek; week++) {
          weekSet.add(week);
        }
      } catch (error) {
        console.error('Error calculating current week:', error);
      }
    }
    
    return Array.from(weekSet).map(Number).sort((a, b) => a - b);
  }, [submissions, assignment]);

  // Group submissions by week number
  const getGroupedSubmissions = useCallback(() => {
    let filtered = getFilteredSubmissions();
    const isWeeklySubmission = !!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true);
    
    // For weekly submissions, always filter by selected week tab
    if (isWeeklySubmission) {
      // For weekly assignments with a specific week selected, show ALL enrolled students for that week
      // Include students who submitted for that week AND students who haven't
      
      // Get ALL submissions for this specific week (before applying search/filter)
      // This includes both submitted submissions AND drafts (submitted_at is NULL but has content/week_number)
      // We need to work with the full submissions array, not the filtered one
      const weekSubmissions = submissions.filter(sub => sub.week_number === selectedWeekTab);
      
      // Get all enrolled students who have a submission (submitted or draft) for this week
      const enrolledStudentIds = new Set(weekSubmissions.map(s => s.user_id));
      
      // Add students who haven't submitted OR drafted for this week
      // These are students with no submission record at all for this week
      const studentsNotSubmittedForWeek = allEnrolledStudents
        .filter(student => !enrolledStudentIds.has(student.user_id))
        .map(student => ({
          id: `not-submitted-${student.user_id}-week-${selectedWeekTab}`,
          user_id: student.user_id,
          first_name: student.first_name,
          last_name: student.last_name,
          email: student.email,
          submitted_at: null,
          status: 'not_submitted',
          grade: null,
          feedback: null,
          content: null,
          pdf_attachment: null,
          week_number: selectedWeekTab // Set week_number so filtering works correctly
        }));
      
      // Mark drafts appropriately - submissions with week_number but no submitted_at are drafts
      const weekSubmissionsWithStatus = weekSubmissions.map(sub => {
        // If submitted_at is null but has content or pdf_attachment, it's a draft
        if (!sub.submitted_at && (sub.content || sub.pdf_attachment)) {
          return {
            ...sub,
            status: 'draft' // Ensure status is set to 'draft'
          };
        }
        return sub;
      });
      
      // Combine submissions (including drafts) and non-submitted students for this week
      const allStudentsForWeek = [...weekSubmissionsWithStatus, ...studentsNotSubmittedForWeek];
      
      // Apply search and status filters to the combined list
      let result = allStudentsForWeek;
      
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        result = result.filter(s => {
          const name = `${cleanName(s.first_name)} ${cleanName(s.last_name)}`.toLowerCase();
          const email = s.email.toLowerCase();
          const grade = s.grade ? s.grade.toString() : '';
          return name.includes(term) || email.includes(term) || grade.includes(term);
        });
      }
      
      // Status filter
      if (filterStatus !== 'all') {
        result = result.filter(s => {
          switch (filterStatus) {
            case 'submitted':
              return s.submitted_at && getEffectiveStatus(s) !== 'graded';
            case 'not_submitted':
              // Not submitted means no submission record at all (not even a draft)
              return !s.submitted_at && !s.content && !s.pdf_attachment;
            case 'draft':
              // Draft means has content/pdf but no submitted_at
              return !s.submitted_at && (s.content || s.pdf_attachment);
            case 'graded':
              return getEffectiveStatus(s) === 'graded';
            case 'ungraded':
              return s.submitted_at && getEffectiveStatus(s) !== 'graded';
            case 'late':
              return getEffectiveStatus(s) === 'late';
            default:
              return true;
          }
        });
      }
      
      // Sort
      result.sort((a, b) => {
        switch (sortOrder) {
          case 'date-desc':
            return new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0);
          case 'date-asc':
            return new Date(a.submitted_at || 0) - new Date(b.submitted_at || 0);
          case 'name-asc':
            return `${cleanName(a.first_name)} ${cleanName(a.last_name)}`.localeCompare(`${cleanName(b.first_name)} ${cleanName(b.last_name)}`);
          case 'name-desc':
            return `${cleanName(b.first_name)} ${cleanName(b.last_name)}`.localeCompare(`${cleanName(a.first_name)} ${cleanName(a.last_name)}`);
          default:
            return 0;
        }
      });
      
      return {
        grouped: false,
        submissions: result
      };
    }
    
    // For non-weekly submissions, return as a single group
    return {
      grouped: false,
      submissions: filtered
    };
  }, [getFilteredSubmissions, assignment, selectedWeekTab, allEnrolledStudents, searchTerm, filterStatus, sortOrder, submissions, getEffectiveStatus]);

  // Calculate stats (week-specific if tab is selected)
  const calculateStats = useCallback(() => {
    const isWeeklySubmission = !!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true);
    
    let filteredSubmissions = submissions;
    
    // For weekly submissions, calculate stats for the selected week
    if (isWeeklySubmission && selectedWeekTab) {
      // Get all submissions for this specific week
      const weekSubmissions = submissions.filter(s => s.week_number === selectedWeekTab);
      
      // Get all enrolled students who have a submission (submitted or draft) for this week
      const enrolledStudentIds = new Set(weekSubmissions.map(s => s.user_id));
      
      // Add students who haven't submitted for this week
      const studentsNotSubmittedForWeek = allEnrolledStudents
        .filter(student => !enrolledStudentIds.has(student.user_id))
        .map(student => ({
          user_id: student.user_id,
          submitted_at: null,
          status: 'not_submitted',
          week_number: selectedWeekTab
        }));
      
      // Combine submissions and non-submitted students for this week
      filteredSubmissions = [...weekSubmissions, ...studentsNotSubmittedForWeek];
    }
    
    const total = filteredSubmissions.length;
    const submitted = filteredSubmissions.filter(s => s.submitted_at).length;
    const graded = filteredSubmissions.filter(s => getEffectiveStatus(s) === 'graded').length;
    const pending = submitted - graded;
    const onTime = filteredSubmissions.filter(s => getEffectiveStatus(s) === 'submitted').length;
    const late = filteredSubmissions.filter(s => getEffectiveStatus(s) === 'late').length;
    const notSubmitted = total - submitted;

    return { total, submitted, graded, pending, onTime, late, notSubmitted };
  }, [submissions, assignment, selectedWeekTab, allEnrolledStudents, getEffectiveStatus]);

  // Get status info
  const getStatusInfo = (submission) => {
    if (!submission.id || submission.id.toString().startsWith('not-submitted-')) {
      return { status: 'not_submitted', label: 'NOT SUBMITTED' };
    }
    
    if (!submission.submitted_at) {
      return { status: 'draft', label: 'DRAFT' };
    }

    const effectiveStatus = getEffectiveStatus(submission);

    if (effectiveStatus === 'graded') {
      return { status: 'graded', label: 'GRADED' };
    }
    
    if (effectiveStatus === 'late') {
      return { status: 'late', label: 'LATE' };
    }
    
    return { status: 'submitted', label: 'SUBMITTED' };
  };

  // Get initials
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}` || '?';
  };

  // Handle assessment save (grade + feedback)
  const handleSaveAssessment = async (submissionId) => {
    try {
      // Get current values
      const grade = editingGrade[submissionId] 
        ? gradingData[submissionId] 
        : selectedSubmission?.grade;
      const feedback = editingFeedback[submissionId]
        ? feedbackData[submissionId]
        : selectedSubmission?.feedback;

      // Validate grade
      const gradeNum = parseFloat(grade);
      if (isNaN(gradeNum) || gradeNum < 0 || gradeNum > assignment.points) {
        setValidationErrors(prev => ({ 
          ...prev, 
          [submissionId]: `Grade must be between 0 and ${assignment.points}` 
        }));
        return;
      }

      // Clear validation errors
      setValidationErrors(prev => ({ ...prev, [submissionId]: null }));
      setSavingAssessment(prev => ({ ...prev, [submissionId]: true }));

      const response = await apiService.gradeAssignment(assignmentId, submissionId, {
        grade: gradeNum,
        feedback: feedback || ''
      });

      if (response.success) {
        setEditingGrade(prev => ({ ...prev, [submissionId]: false }));
        setEditingFeedback(prev => ({ ...prev, [submissionId]: false }));
        setLastSaved(prev => ({ ...prev, [submissionId]: new Date() }));
        await fetchData();
      } else {
        setValidationErrors(prev => ({ 
          ...prev, 
          [submissionId]: response.error || 'Failed to save assessment' 
        }));
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      setValidationErrors(prev => ({ 
        ...prev, 
        [submissionId]: 'Failed to save assessment. Please try again.' 
      }));
    } finally {
      setSavingAssessment(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  // Check if assessment has unsaved changes
  const hasUnsavedChanges = (submissionId) => {
    return editingGrade[submissionId] || editingFeedback[submissionId];
  };

  // Handle allowing student to resubmit
  const handleAllowResubmission = async (submissionId) => {
    const submission = submissions.find(s => s.id === submissionId);
    if (!submission || !submission.user_id) {
      console.error('Submission not found or missing user_id');
      return;
    }

    const isWeeklySubmission = !!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true);
    const weekInfo = isWeeklySubmission && submission.week_number != null && submission.week_number > 0
      ? ` for Week ${submission.week_number}` 
      : '';

    try {
      setAllowingResubmission(prev => ({ ...prev, [submissionId]: true }));
      setResubmissionStatus(prev => ({ ...prev, [submissionId]: null }));

      const response = await apiService.allowStudentResubmission(assignmentId, submission.user_id, submission.week_number || null);

      if (response.success) {
        setResubmissionStatus(prev => ({ 
          ...prev, 
          [submissionId]: { 
            type: 'success', 
            message: `Student can now edit and resubmit this assignment${weekInfo}. Grade and feedback will be cleared when they resubmit.` 
          }
        }));
        // Refresh data to get updated resubmission status
        await fetchData();
      } else {
        setResubmissionStatus(prev => ({ 
          ...prev, 
          [submissionId]: { type: 'error', message: response.error || 'Failed to allow resubmission' }
        }));
      }
    } catch (error) {
      console.error('Error allowing resubmission:', error);
      setResubmissionStatus(prev => ({ 
        ...prev, 
        [submissionId]: { type: 'error', message: 'Failed to allow resubmission. Please try again.' }
      }));
    } finally {
      setAllowingResubmission(prev => ({ ...prev, [submissionId]: false }));
      // Clear status message after 5 seconds
      setTimeout(() => {
        setResubmissionStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[submissionId];
          return newStatus;
        });
      }, 5000);
    }
  };

  // Format last saved time
  const getLastSavedText = (submissionId) => {
    const savedTime = lastSaved[submissionId];
    if (!savedTime) return null;
    
    const seconds = Math.floor((new Date() - savedTime) / 1000);
    if (seconds < 10) return 'Saved just now';
    if (seconds < 60) return `Saved ${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Saved ${minutes}m ago`;
    return 'Saved';
  };

  // Navigation handlers
  const handleSelectStudent = (studentId) => {
    setSelectedStudentId(studentId);
    setMobileShowDetail(true);
  };

  const handleBackToList = () => {
    setMobileShowDetail(false);
  };

  // Render functions
  const submissionGroups = getGroupedSubmissions();
  const stats = calculateStats();
  const selectedSubmission = submissions.find(s => s.id === selectedStudentId);
  
  // Flatten grouped submissions for navigation
  const allSubmissionsFlat = submissionGroups.grouped
    ? submissionGroups.weekGroups.flatMap(group => group.submissions)
    : submissionGroups.submissions;
  
  const selectedIndex = allSubmissionsFlat.findIndex(s => s.id === selectedStudentId);
  const canGoNext = selectedIndex < allSubmissionsFlat.length - 1;
  const canGoPrev = selectedIndex > 0;

  const handleNextStudent = () => {
    if (canGoNext && allSubmissionsFlat[selectedIndex + 1]) {
      setSelectedStudentId(allSubmissionsFlat[selectedIndex + 1].id);
    }
  };

  const handlePrevStudent = () => {
    if (canGoPrev && allSubmissionsFlat[selectedIndex - 1]) {
      setSelectedStudentId(allSubmissionsFlat[selectedIndex - 1].id);
    }
  };

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.emptyState}>
          <FiAlertCircle />
          <p>Assignment not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.header}>
        {fromPendingGrading && (
          <button className={styles.backButton} onClick={() => navigate('/instructor/pending-grading')}>
            <FiArrowLeft /> Back to Pending Grading
          </button>
        )}
        <h1 className={styles.title}>{assignment.title}</h1>
        <div className={styles.subtitle}>
          <span>
            <FiFileText /> {assignment.course_code} - {assignment.course_name}
          </span>
          {assignment.due_date && (
            <span>
              <FiClock /> Due: {formatUTCToChicago(assignment.due_date)}
            </span>
          )}
          <span>
            <FiAward /> {assignment.points} points
          </span>
        </div>
      </div>

      {/* Stats Strip */}
      <div className={styles.statsStrip}>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.total}</div>
          <div className={styles.statLabel}>Total Students</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.submitted}</div>
          <div className={styles.statLabel}>Submitted</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.graded}</div>
          <div className={styles.statLabel}>Graded</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.pending}</div>
          <div className={styles.statLabel}>Pending</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.onTime}</div>
          <div className={styles.statLabel}>On Time</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.late}</div>
          <div className={styles.statLabel}>Late</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className={styles.filterSelect} 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Submissions</option>
          <option value="submitted">Submitted</option>
          <option value="not_submitted">Not Submitted</option>
          <option value="graded">Graded</option>
          <option value="ungraded">Pending Grade</option>
          <option value="late">Late</option>
        </select>
        <select 
          className={styles.filterSelect} 
          value={sortOrder} 
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="date-desc">Newest First</option>
          <option value="date-asc">Oldest First</option>
          <option value="name-asc">Name A-Z</option>
          <option value="name-desc">Name Z-A</option>
        </select>
      </div>

      {/* Week Tabs (for weekly assignments only) */}
      {!!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true) && (
        <div className={styles.weekTabsContainer}>
          <div className={styles.weekTabs}>
            {getAvailableWeeks().map(weekNum => {
              // Count includes all enrolled students (they all appear in the week tab)
              const totalForWeek = allEnrolledStudents.length;
              
              return (
                <button
                  key={weekNum}
                  className={`${styles.weekTab} ${selectedWeekTab === weekNum ? styles.active : ''}`}
                  onClick={() => {
                    setSelectedWeekTab(weekNum);
                    setSelectedStudentId(null); // Clear selection when switching tabs
                  }}
                >
                  Week {weekNum}
                  <span className={styles.tabCount}>{totalForWeek}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Two-pane content */}
      <div className={styles.content}>
        {/* Left Pane: Student List */}
        <div className={`${styles.leftPane} ${mobileShowDetail ? styles.mobileHidden : ''}`}>
          {submissionGroups.submissions.length === 0 ? (
            <div className={styles.emptyState}>
              <FiFileText />
              <p>No submissions match your filters</p>
            </div>
          ) : (
            submissionGroups.submissions.map((submission) => {
              const statusInfo = getStatusInfo(submission);
              const isActive = submission.id === selectedStudentId;

              return (
                <div
                  key={submission.id}
                  className={`${styles.studentItem} ${isActive ? styles.active : ''}`}
                  onClick={() => handleSelectStudent(submission.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectStudent(submission.id);
                    }
                  }}
                >
                  <div className={styles.avatar}>
                    {getInitials(cleanName(submission.first_name), cleanName(submission.last_name))}
                  </div>
                  <div className={styles.studentInfo}>
                    <div className={styles.studentName}>
                      {cleanName(submission.first_name)} {cleanName(submission.last_name)}
                    </div>
                    <div className={styles.studentEmail}>{submission.email}</div>
                  </div>
                  <div className={styles.studentMeta}>
                    <span className={`${styles.statusPill} ${styles[statusInfo.status]}`}>
                      {statusInfo.label}
                    </span>
                    {submission.grade !== null && submission.grade !== undefined && (
                      <span className={styles.gradeBadge}>{submission.grade}/{assignment.points}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Pane: Detail View */}
        <div className={`${styles.rightPane} ${mobileShowDetail ? styles.mobileVisible : ''}`}>
          {!selectedSubmission ? (
            <div className={styles.emptyState}>
              <FiFileText />
              <p>Select a student to view their submission</p>
            </div>
          ) : (
            <div className={styles.submissionPage}>
              {/* Detail Header */}
              <div className={styles.detailHeader}>
                <div className={styles.detailStudent}>
                  <div className={styles.detailAvatar}>
                    {getInitials(cleanName(selectedSubmission.first_name), cleanName(selectedSubmission.last_name))}
                  </div>
                  <div className={styles.detailInfo}>
                    <h3>
                      {cleanName(selectedSubmission.first_name)} {cleanName(selectedSubmission.last_name)}
                      {!!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true) && selectedSubmission.week_number != null && selectedSubmission.week_number > 0 && (
                        <span style={{ 
                          marginLeft: '0.75rem',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: 'var(--accent-color)',
                          padding: '0.25rem 0.625rem',
                          background: 'rgba(216, 184, 137, 0.1)',
                          border: '1px solid rgba(216, 184, 137, 0.2)',
                          borderRadius: '8px'
                        }}>
                          Week {selectedSubmission.week_number}
                        </span>
                      )}
                    </h3>
                    <p>{selectedSubmission.email}</p>
                    <div className={styles.detailSubmittedAt}>
                      {selectedSubmission.submitted_at
                        ? `Submitted: ${formatUTCToChicago(selectedSubmission.submitted_at)}`
                        : 'Not submitted yet'}
                    </div>
                  </div>
                </div>
                <div className={styles.navButtons}>
                  <button 
                    className={styles.navButton} 
                    onClick={handlePrevStudent}
                    disabled={!canGoPrev}
                    aria-label="Previous student"
                  >
                    <FiChevronLeft />
                  </button>
                  <button 
                    className={styles.navButton} 
                    onClick={handleNextStudent}
                    disabled={!canGoNext}
                    aria-label="Next student"
                  >
                    <FiChevronRight />
                  </button>
                  {/* Mobile back button */}
                  <button 
                    className={`${styles.navButton} ${styles.mobileOnly}`}
                    onClick={handleBackToList}
                    aria-label="Back to list"
                  >
                    <FiArrowLeft />
                  </button>
                </div>
              </div>

              {/* Resubmission Control Section - Moved to top */}
              {selectedSubmission.submitted_at && (
                <div className={`${styles.sectionCard} ${styles.resubmissionCard}`}>
                  <div className={styles.sectionBody}>
                    <div className={styles.resubmissionHeader}>
                      <div className={styles.resubmissionInfo}>
                        <h4 className={styles.resubmissionTitle}>
                          Resubmission Control
                          {!!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true) && selectedSubmission.week_number != null && selectedSubmission.week_number > 0 && (
                            <span style={{ 
                              marginLeft: '0.5rem',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'var(--text-secondary)'
                            }}>
                              (Week {selectedSubmission.week_number})
                            </span>
                          )}
                        </h4>
                        <p className={styles.resubmissionHelper}>
                          {!!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true) && selectedSubmission.week_number != null && selectedSubmission.week_number > 0
                            ? `Allow student to edit and resubmit Week ${selectedSubmission.week_number}`
                            : 'Allow student to edit and resubmit this assignment'}
                        </p>
                      </div>
                      <button
                        className={`${styles.resubmissionButton} ${selectedSubmission.resubmission_state === 'open_for_resubmission' ? styles.enabled : ''}`}
                        onClick={() => handleAllowResubmission(selectedSubmission.id)}
                        disabled={allowingResubmission[selectedSubmission.id] || 
                                 selectedSubmission.resubmission_state === 'open_for_resubmission'}
                      >
                        {allowingResubmission[selectedSubmission.id] ? (
                          <>
                            <span className={styles.buttonSpinner}></span>
                            Enabling...
                          </>
                        ) : selectedSubmission.resubmission_state === 'open_for_resubmission' ? (
                          <>
                            <FiUnlock size={16} />
                            Resubmission Enabled
                          </>
                        ) : (
                          <>
                            <FiEdit3 size={16} />
                            Allow Resubmission
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* Resubmission Status */}
                    {resubmissionStatus[selectedSubmission.id] ? (
                      <div className={`${styles.resubmissionStatus} ${styles[resubmissionStatus[selectedSubmission.id].type]}`}>
                        {resubmissionStatus[selectedSubmission.id].type === 'success' ? (
                          <FiCheckCircle size={16} />
                        ) : (
                          <FiAlertCircle size={16} />
                        )}
                        {resubmissionStatus[selectedSubmission.id].message}
                      </div>
                    ) : selectedSubmission.resubmission_state === 'open_for_resubmission' ? (
                      <div className={`${styles.resubmissionStatus} ${styles.success}`}>
                        <FiCheckCircle size={16} />
                        {!!(assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true) && selectedSubmission.week_number != null && selectedSubmission.week_number > 0
                          ? `Student can now edit and resubmit this assignment for Week ${selectedSubmission.week_number}. Grade and feedback will be cleared when they resubmit.`
                          : 'Student can now edit and resubmit this assignment. Grade and feedback will be cleared when they resubmit.'}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Submission Preview */}
              <div className={`${styles.sectionCard} ${styles.submissionPreview}`}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.previewHeader}>Submission</h3>
                </div>
                <div className={styles.sectionBody}>
                  {!selectedSubmission.submitted_at ? (
                    <div className={styles.emptyState}>
                      <FiAlertCircle />
                      <p>Student has not submitted yet</p>
                    </div>
                  ) : (
                    <div className={styles.submissionContent}>
                      {selectedSubmission.content && (
                        <div 
                          className={`${styles.rtPreview} ${richTextStyles.richText}`}
                          dangerouslySetInnerHTML={{ __html: selectedSubmission.content }}
                        />
                      )}
                      
                      {selectedSubmission.pdf_attachment && (
                        <div className={styles.pdfPreview}>
                          <button
                            className={styles.pdfButton}
                            onClick={() => {
                              setSelectedPdf(selectedSubmission.pdf_attachment);
                              const studentName = `${cleanName(selectedSubmission.first_name || '')}_${cleanName(selectedSubmission.last_name || '')}`.trim().replace(/\s+/g, '_') || 'student';
                              const assignmentTitle = assignment?.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'assignment';
                              setSelectedPdfFileName(`${studentName}_${assignmentTitle}_submission.pdf`);
                              setShowPdfViewer(true);
                            }}
                          >
                            <FiEye /> View PDF Attachment
                          </button>
                        </div>
                      )}
                      
                      {!selectedSubmission.content && !selectedSubmission.pdf_attachment && (
                        <div className={styles.emptyState}>
                          <FiFileText />
                          <p>No submission content available</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Assessment Card - Unified Grading + Feedback */}
              {selectedSubmission.submitted_at && (
                <div className={`${styles.sectionCard} ${styles.assessmentCard}`}>
                  <div className={`${styles.sectionHeader} ${styles.assessmentHeader}`}>
                    <h3 className={styles.assessmentTitle}>Assessment</h3>
                    {lastSaved[selectedSubmission.id] && !hasUnsavedChanges(selectedSubmission.id) && (
                      <span className={styles.savedIndicator}>
                        <FiCheckCircle /> {getLastSavedText(selectedSubmission.id)}
                      </span>
                    )}
                    {hasUnsavedChanges(selectedSubmission.id) && (
                      <span className={styles.unsavedIndicator}>
                        <FiAlertCircle /> Unsaved changes
                      </span>
                    )}
                  </div>

                  <div className={styles.sectionBody}>
                    <div className={styles.assessmentBody}>
                      <div className={styles.gradeGroup}>
                        <label htmlFor={`grade-${selectedSubmission.id}`} className={styles.inputLabel}>
                          Grade
                          <span className={styles.labelHelper}>0–{assignment.points}</span>
                        </label>
                        <input
                          id={`grade-${selectedSubmission.id}`}
                          type="number"
                          className={`${styles.gradeInput} ${validationErrors[selectedSubmission.id] ? styles.inputError : ''}`}
                          value={editingGrade[selectedSubmission.id] 
                            ? gradingData[selectedSubmission.id] ?? '' 
                            : selectedSubmission.grade ?? ''}
                          onChange={(e) => {
                            setEditingGrade(prev => ({ ...prev, [selectedSubmission.id]: true }));
                            setGradingData(prev => ({ ...prev, [selectedSubmission.id]: e.target.value }));
                            setValidationErrors(prev => ({ ...prev, [selectedSubmission.id]: null }));
                          }}
                          placeholder="0.0"
                          min="0"
                          max={assignment.points}
                          step="0.5"
                          aria-invalid={!!validationErrors[selectedSubmission.id]}
                          aria-describedby={validationErrors[selectedSubmission.id] ? `grade-error-${selectedSubmission.id}` : undefined}
                        />
                        {validationErrors[selectedSubmission.id] && (
                          <span id={`grade-error-${selectedSubmission.id}`} className={styles.errorMessage}>
                            <FiAlertCircle /> {validationErrors[selectedSubmission.id]}
                          </span>
                        )}
                      </div>

                      <div className={styles.feedbackGroup}>
                        <label htmlFor={`feedback-${selectedSubmission.id}`} className={styles.inputLabel}>
                          Feedback
                          <span className={styles.labelHelper}>Optional</span>
                        </label>
                        <textarea
                          id={`feedback-${selectedSubmission.id}`}
                          className={styles.feedbackTextarea}
                          value={editingFeedback[selectedSubmission.id]
                            ? feedbackData[selectedSubmission.id] ?? ''
                            : selectedSubmission.feedback ?? ''}
                          onChange={(e) => {
                            setEditingFeedback(prev => ({ ...prev, [selectedSubmission.id]: true }));
                            setFeedbackData(prev => ({ ...prev, [selectedSubmission.id]: e.target.value }));
                          }}
                          placeholder="Provide feedback to help the student improve..."
                          rows="4"
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.sectionFooter}>
                    <button
                      className={styles.saveAssessmentButton}
                      onClick={() => handleSaveAssessment(selectedSubmission.id)}
                      disabled={!hasUnsavedChanges(selectedSubmission.id) || savingAssessment[selectedSubmission.id]}
                    >
                      {savingAssessment[selectedSubmission.id] ? (
                        <>
                          <span className={styles.buttonSpinner}></span>
                          Saving...
                        </>
                      ) : (
                        'Save Assessment'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PDF Viewer */}
      <PdfViewer
        isOpen={showPdfViewer}
        onClose={() => {
          setShowPdfViewer(false);
          setSelectedPdf(null);
          setSelectedPdfFileName('document.pdf');
        }}
        pdfBase64={selectedPdf}
        fileName={selectedPdfFileName}
      />
    </div>
  );
}

export default AssignmentSubmissions;
